/** Eight certificate-depth examples per part for Eigenvalues & Eigenvectors. */

export const LA_EIGEN_P1_EXAMPLES = [
  {
    number: 1,
    tier: "Basic",
    title: "Eigenvalues of a $2\\times2$ matrix",
    setup: "Find the eigenvalues of $A=\\begin{pmatrix}4&1\\\\2&3\\end{pmatrix}$.",
    steps: [
      { text: "Eigenvalues solve $\\det(A-\\lambda I)=0$.", why: "A nonzero eigenvector needs a singular matrix." },
      { text: "$A-\\lambda I=\\begin{pmatrix}4-\\lambda&1\\\\2&3-\\lambda\\end{pmatrix}$.", why: "Subtract $\\lambda$ on the diagonal only." },
      { text: "$\\det=(4-\\lambda)(3-\\lambda)-1(2)$.", why: "$2\\times2$ determinant." },
      { text: "Expand: $12-7\\lambda+\\lambda^{2}-2$.", why: "Multiply out." },
      { text: "Characteristic polynomial: $\\lambda^{2}-7\\lambda+10$.", why: "Collect terms." },
      { text: "Factor: $(\\lambda-5)(\\lambda-2)=0$.", why: "Look for integer roots first." },
      { text: "So $\\lambda_1=5$, $\\lambda_2=2$.", why: "Two distinct real eigenvalues." },
      { text: "Consistency: $\\lambda_1+\\lambda_2=7=\\operatorname{tr}A$ and $\\lambda_1\\lambda_2=10=\\det A$.", why: "Trace and determinant checks." },
    ],
    result: "$\\lambda=5$ and $\\lambda=2$",
    check: "$\\operatorname{tr}A=4+3=7$, $\\det A=12-2=10$ — both match.",
    mistake: "Subtracting $\\lambda$ from every entry instead of only the diagonal.",
    life: "In structural vibration, the generalized eigenproblem $K\\phi=\\lambda M\\phi$ gives $\\lambda=\\omega^2$, so each natural angular frequency is $\\omega=\\sqrt{\\lambda}$. Engineers avoid sustained forcing near the corresponding natural frequencies.",
  },
  {
    number: 2,
    tier: "Basic",
    title: "Eigenvectors for each eigenvalue",
    setup: "For the matrix in Example 1, find an eigenvector for $\\lambda=5$ and for $\\lambda=2$.",
    steps: [
      { text: "For $\\lambda=5$: $A-5I=\\begin{pmatrix}-1&1\\\\2&-2\\end{pmatrix}$.", why: "Substitute the eigenvalue." },
      { text: "Solve $(A-5I)v=0$: row $1$ gives $-v_1+v_2=0$.", why: "One equation is enough; the rows are dependent." },
      { text: "So $v_2=v_1$; take $v=(1,1)$.", why: "Eigenvectors are determined up to scale." },
      { text: "Verify: $A(1,1)^{T}=(4+1,\\,2+3)=(5,5)=5(1,1)$.", why: "Definition satisfied." },
      { text: "For $\\lambda=2$: $A-2I=\\begin{pmatrix}2&1\\\\2&1\\end{pmatrix}$.", why: "Second eigenvalue." },
      { text: "Row $1$: $2v_1+v_2=0$, so $v_2=-2v_1$.", why: "Solve the homogeneous system." },
      { text: "Take $v=(1,-2)$.", why: "Simplest integer choice." },
      { text: "Verify: $A(1,-2)^{T}=(4-2,\\,2-6)=(2,-4)=2(1,-2)$.", why: "Confirmed." },
    ],
    result: "$v_{\\lambda=5}=(1,1)$ and $v_{\\lambda=2}=(1,-2)$",
    check: "The two eigenvectors are independent, since $\\det\\begin{pmatrix}1&1\\\\1&-2\\end{pmatrix}=-3\\ne0$.",
    mistake: "Reporting $v=0$; the zero vector is never an eigenvector.",
  },
  {
    number: 3,
    tier: "Medium",
    title: "Complex eigenvalues from a rotation",
    setup: "Find the eigenvalues of the rotation matrix $R=\\begin{pmatrix}0&-1\\\\1&0\\end{pmatrix}$.",
    steps: [
      { text: "$\\det(R-\\lambda I)=\\det\\begin{pmatrix}-\\lambda&-1\\\\1&-\\lambda\\end{pmatrix}$.", why: "Set up the characteristic equation." },
      { text: "$=\\lambda^{2}-(-1)(1)=\\lambda^{2}+1$.", why: "Careful with the two negatives." },
      { text: "Solve $\\lambda^{2}+1=0$: $\\lambda=\\pm i$.", why: "No real roots." },
      { text: "Discriminant $=-4<0$, confirming a complex-conjugate pair.", why: "Real matrices give conjugate pairs." },
      { text: "Geometric reason: a $90^\\circ$ rotation fixes no real direction.", why: "So no real eigenvector can exist." },
      { text: "Trace $=0=i+(-i)$ and determinant $=1=i(-i)$.", why: "Checks still work over $\\mathbb{C}$." },
      { text: "$|\\lambda|=1$, matching the fact that rotations preserve length.", why: "Modulus is the scaling factor." },
      { text: "An eigenvector for $\\lambda=i$ is $(1,-i)$: $R(1,-i)^{T}=(i,1)=i(1,-i)$.", why: "Complex eigenvector." },
    ],
    result: "$\\lambda=\\pm i$, with $|\\lambda|=1$ and no real eigenvectors",
    check: "$R^{2}=-I$, consistent with $\\lambda^{2}=-1$.",
    mistake: "Declaring \"no eigenvalues\" instead of complex eigenvalues.",
  },
  {
    number: 4,
    tier: "Medium",
    title: "Repeated eigenvalue, defective matrix",
    setup: "Analyze $A=\\begin{pmatrix}3&1\\\\0&3\\end{pmatrix}$.",
    steps: [
      { text: "$A$ is upper triangular, so its eigenvalues are the diagonal entries.", why: "Triangular shortcut." },
      { text: "$\\det(A-\\lambda I)=(3-\\lambda)^{2}$, so $\\lambda=3$ with algebraic multiplicity $2$.", why: "Double root." },
      { text: "$A-3I=\\begin{pmatrix}0&1\\\\0&0\\end{pmatrix}$.", why: "Now find the eigenspace." },
      { text: "Solve $(A-3I)v=0$: the equation is $v_2=0$.", why: "Only one constraint." },
      { text: "So $v=(1,0)$ spans the eigenspace: geometric multiplicity $1$.", why: "One free variable." },
      { text: "Geometric $<$ algebraic multiplicity, so $A$ is defective and not diagonalizable.", why: "The diagonalizability criterion." },
      { text: "Verify: $A(1,0)^{T}=(3,0)=3(1,0)$.", why: "Eigenvector confirmed." },
      { text: "The Jordan form is $\\begin{pmatrix}3&1\\\\0&3\\end{pmatrix}$ itself — a single $2\\times2$ Jordan block.", why: "Best possible normal form here." },
    ],
    result: "$\\lambda=3$ (multiplicity $2$) with a single eigenvector direction $(1,0)$; $A$ is not diagonalizable",
    check: "$(A-3I)^{2}=0$, showing the nilpotent part of the Jordan block.",
    mistake: "Assuming a repeated eigenvalue always gives two independent eigenvectors.",
  },
  {
    number: 5,
    tier: "Medium",
    title: "$3\\times3$ eigenvalues with a triangular block",
    setup: "Find the eigenvalues and eigenvectors of $A=\\begin{pmatrix}2&0&0\\\\1&3&0\\\\0&1&4\\end{pmatrix}$.",
    steps: [
      { text: "$A$ is lower triangular, so $\\lambda=2,3,4$.", why: "Diagonal entries are the eigenvalues." },
      { text: "For $\\lambda=4$: $(A-4I)v=0$ gives $-2v_1=0$, $v_1-v_2=0$, $v_2=0$.", why: "Row by row." },
      { text: "So $v_1=v_2=0$ and $v_3$ is free: $v=(0,0,1)$.", why: "Eigenvector for the largest eigenvalue." },
      { text: "For $\\lambda=3$: rows give $-v_1=0$, $v_1+0=0$, $v_2+v_3=0$.", why: "Substitute $\\lambda=3$." },
      { text: "So $v_1=0$, $v_3=-v_2$: $v=(0,1,-1)$.", why: "One free parameter." },
      { text: "For $\\lambda=2$: rows give $0=0$, $v_1+v_2=0$, $v_2+2v_3=0$.", why: "Substitute $\\lambda=2$." },
      { text: "Take $v_3=1$: $v_2=-2$, $v_1=2$, so $v=(2,-2,1)$.", why: "Back-substitute." },
      { text: "Three distinct eigenvalues give three independent eigenvectors, so $A$ is diagonalizable.", why: "Distinct eigenvalues always are." },
    ],
    result: "$\\lambda=2,3,4$ with eigenvectors $(2,-2,1)$, $(0,1,-1)$, $(0,0,1)$",
    check: "$A(0,1,-1)^{T}=(0,3,1-4)=(0,3,-3)=3(0,1,-1)$.",
    mistake: "Expanding a full characteristic polynomial when the matrix is already triangular.",
  },
  {
    number: 6,
    tier: "Hard",
    title: "Symmetric matrix has orthogonal eigenvectors",
    setup: "Diagonalize the directions of $A=\\begin{pmatrix}2&1\\\\1&2\\end{pmatrix}$ and verify orthogonality.",
    steps: [
      { text: "$\\det(A-\\lambda I)=(2-\\lambda)^{2}-1$.", why: "Characteristic polynomial." },
      { text: "$=\\lambda^{2}-4\\lambda+3=(\\lambda-3)(\\lambda-1)$.", why: "Factor." },
      { text: "So $\\lambda=3$ and $\\lambda=1$, both real — as guaranteed for symmetric matrices.", why: "Spectral theorem." },
      { text: "For $\\lambda=3$: $\\begin{pmatrix}-1&1\\\\1&-1\\end{pmatrix}v=0$ gives $v=(1,1)$.", why: "Solve the homogeneous system." },
      { text: "For $\\lambda=1$: $\\begin{pmatrix}1&1\\\\1&1\\end{pmatrix}v=0$ gives $v=(1,-1)$.", why: "Second eigenvector." },
      { text: "Orthogonality: $(1,1)\\cdot(1,-1)=1-1=0$.", why: "Automatic for symmetric matrices with distinct eigenvalues." },
      { text: "Normalize: $q_1=\\tfrac{1}{\\sqrt2}(1,1)$, $q_2=\\tfrac{1}{\\sqrt2}(1,-1)$.", why: "Build an orthonormal basis." },
      { text: "Then $A=Q\\Lambda Q^{T}$ with $Q=[q_1\\ q_2]$ and $\\Lambda=\\operatorname{diag}(3,1)$.", why: "Orthogonal diagonalization." },
    ],
    result: "$\\lambda=3,1$ with orthogonal eigenvectors $(1,1)$ and $(1,-1)$; $A=Q\\Lambda Q^{T}$",
    check: "$Q^{T}Q=I$ and $\\operatorname{tr}A=4=3+1$.",
    mistake: "Using $Q^{-1}$ computed the long way when $Q^{T}$ already inverts an orthonormal $Q$.",
  },
  {
    number: 7,
    tier: "Hard",
    title: "Eigenvalues of $A^{2}$, $A^{-1}$, and $A+cI$",
    setup: "$A$ is $2\\times2$ with eigenvalues $2$ and $-3$. Find the spectra of $A^{2}$, $A^{-1}$, $A+4I$, and $A^{T}$.",
    steps: [
      { text: "If $Av=\\lambda v$ then $A^{2}v=A(\\lambda v)=\\lambda^{2}v$.", why: "Same eigenvector, squared eigenvalue." },
      { text: "So $A^{2}$ has eigenvalues $4$ and $9$.", why: "Square each." },
      { text: "$A$ is invertible because $0$ is not an eigenvalue.", why: "Zero eigenvalue is the singularity test." },
      { text: "From $Av=\\lambda v$: $v=\\lambda A^{-1}v$, so $A^{-1}v=\\tfrac{1}{\\lambda}v$.", why: "Invert the relation." },
      { text: "$A^{-1}$ has eigenvalues $\\tfrac12$ and $-\\tfrac13$.", why: "Reciprocals." },
      { text: "$(A+4I)v=\\lambda v+4v=(\\lambda+4)v$, giving eigenvalues $6$ and $1$.", why: "Shifts add to eigenvalues." },
      { text: "$A^{T}$ has the same characteristic polynomial as $A$, so eigenvalues $2$ and $-3$.", why: "$\\det(A^{T}-\\lambda I)=\\det(A-\\lambda I)$." },
      { text: "Sanity: $\\det A=2(-3)=-6$, $\\det(A^{2})=36=4\\cdot9$, $\\det(A^{-1})=-\\tfrac16$.", why: "Determinants are products of eigenvalues." },
    ],
    result: "$A^{2}:\\{4,9\\}$; $A^{-1}:\\{\\tfrac12,-\\tfrac13\\}$; $A+4I:\\{6,1\\}$; $A^{T}:\\{2,-3\\}$",
    check: "$\\operatorname{tr}(A+4I)=\\operatorname{tr}A+8=(-1)+8=7=6+1$.",
    mistake: "Thinking $A+4I$ shifts eigenvectors too; the eigenvectors are unchanged.",
  },
  {
    number: 8,
    tier: "Hard",
    title: "Characteristic polynomial and Cayley–Hamilton",
    setup: "For $A=\\begin{pmatrix}1&2\\\\3&2\\end{pmatrix}$, find $p(\\lambda)$, verify $p(A)=0$, and use it to compute $A^{-1}$.",
    steps: [
      { text: "$p(\\lambda)=\\lambda^{2}-(\\operatorname{tr}A)\\lambda+\\det A$.", why: "$2\\times2$ shortcut." },
      { text: "$\\operatorname{tr}A=3$ and $\\det A=2-6=-4$, so $p(\\lambda)=\\lambda^{2}-3\\lambda-4$.", why: "Plug in." },
      { text: "Roots: $(\\lambda-4)(\\lambda+1)$, so $\\lambda=4,-1$.", why: "Factor." },
      { text: "$A^{2}=\\begin{pmatrix}1+6&2+4\\\\3+6&6+4\\end{pmatrix}=\\begin{pmatrix}7&6\\\\9&10\\end{pmatrix}$.", why: "Direct multiplication." },
      { text: "$p(A)=A^{2}-3A-4I=\\begin{pmatrix}7-3-4&6-6\\\\9-9&10-6-4\\end{pmatrix}=0$.", why: "Cayley–Hamilton verified entrywise." },
      { text: "Rearrange: $A^{2}-3A=4I$, so $A(A-3I)=4I$.", why: "Factor out $A$." },
      { text: "Hence $A^{-1}=\\tfrac14(A-3I)=\\tfrac14\\begin{pmatrix}-2&2\\\\3&-1\\end{pmatrix}$.", why: "Inverse without cofactors." },
      { text: "Check: $AA^{-1}$ entry $(1,1)=\\tfrac14(1(-2)+2(3))=\\tfrac44=1$.", why: "Confirms the inverse." },
    ],
    result: "$p(\\lambda)=\\lambda^{2}-3\\lambda-4$, $p(A)=0$, and $A^{-1}=\\tfrac14\\begin{pmatrix}-2&2\\\\3&-1\\end{pmatrix}$",
    check: "$\\det A^{-1}=\\tfrac{1}{16}(2-6)=-\\tfrac14=1/\\det A$.",
    mistake: "Writing $p(\\lambda)=\\lambda^{2}+\\operatorname{tr}(A)\\lambda+\\det A$; the trace term carries a minus sign.",
  },
];

export const LA_EIGEN_P2_EXAMPLES = [
  {
    number: 1,
    tier: "Basic",
    title: "Diagonalize a $2\\times2$ matrix",
    setup: "Diagonalize $A=\\begin{pmatrix}5&-2\\\\0&3\\end{pmatrix}$, i.e. find $P$ and $D$ with $A=PDP^{-1}$.",
    steps: [
      { text: "$A$ is triangular, so $\\lambda=5$ and $\\lambda=3$.", why: "Read the diagonal." },
      { text: "For $\\lambda=5$: $(A-5I)=\\begin{pmatrix}0&-2\\\\0&-2\\end{pmatrix}$ gives $v_2=0$, so $v=(1,0)$.", why: "Solve the homogeneous system." },
      { text: "For $\\lambda=3$: $(A-3I)=\\begin{pmatrix}2&-2\\\\0&0\\end{pmatrix}$ gives $v_1=v_2$, so $v=(1,1)$.", why: "Second eigenvector." },
      { text: "$P=\\begin{pmatrix}1&1\\\\0&1\\end{pmatrix}$ with eigenvectors as columns.", why: "Column order must match $D$." },
      { text: "$D=\\begin{pmatrix}5&0\\\\0&3\\end{pmatrix}$.", why: "Eigenvalues in the same order." },
      { text: "$\\det P=1\\ne0$, so $P$ is invertible: $P^{-1}=\\begin{pmatrix}1&-1\\\\0&1\\end{pmatrix}$.", why: "Diagonalizability requires independent eigenvectors." },
      { text: "$PD=\\begin{pmatrix}5&3\\\\0&3\\end{pmatrix}$.", why: "First half of the product." },
      { text: "$PDP^{-1}=\\begin{pmatrix}5&-5+3\\\\0&3\\end{pmatrix}=\\begin{pmatrix}5&-2\\\\0&3\\end{pmatrix}=A$.", why: "Verification." },
    ],
    result: "$P=\\begin{pmatrix}1&1\\\\0&1\\end{pmatrix}$, $D=\\operatorname{diag}(5,3)$",
    check: "$\\operatorname{tr}A=8=5+3$ and $\\det A=15$.",
    mistake: "Mismatching the order of eigenvectors in $P$ and eigenvalues in $D$.",
    life: "Diagonalizing turns a coupled system into independent one-dimensional problems — that is how engineers decouple vibration modes or chemical reaction rates.",
  },
  {
    number: 2,
    tier: "Basic",
    title: "Matrix power via diagonalization",
    setup: "Compute $A^{5}$ for $A=\\begin{pmatrix}2&0\\\\1&3\\end{pmatrix}$.",
    steps: [
      { text: "Eigenvalues from the diagonal: $\\lambda=2,3$.", why: "Lower triangular." },
      { text: "For $\\lambda=3$: $(A-3I)=\\begin{pmatrix}-1&0\\\\1&0\\end{pmatrix}$ gives $v_1=0$, so $v=(0,1)$.", why: "Eigenvector." },
      { text: "For $\\lambda=2$: $(A-2I)=\\begin{pmatrix}0&0\\\\1&1\\end{pmatrix}$ gives $v_2=-v_1$, so $v=(1,-1)$.", why: "Second eigenvector." },
      { text: "$A^{5}=PD^{5}P^{-1}$ with $P=\\begin{pmatrix}1&0\\\\-1&1\\end{pmatrix}$, $D=\\operatorname{diag}(2,3)$.", why: "Powers only touch $D$." },
      { text: "$D^{5}=\\operatorname{diag}(32,243)$.", why: "$2^{5}=32$, $3^{5}=243$." },
      { text: "$P^{-1}=\\begin{pmatrix}1&0\\\\1&1\\end{pmatrix}$.", why: "Unit determinant makes this easy." },
      { text: "$PD^{5}=\\begin{pmatrix}32&0\\\\-32&243\\end{pmatrix}$.", why: "Scale the columns." },
      { text: "$A^{5}=PD^{5}P^{-1}=\\begin{pmatrix}32&0\\\\211&243\\end{pmatrix}$.", why: "$-32+243=211$." },
    ],
    result: "$A^{5}=\\begin{pmatrix}32&0\\\\211&243\\end{pmatrix}$",
    check: "Trace $=275=2^{5}+3^{5}$, and the matrix stays lower triangular.",
    mistake: "Raising every entry of $A$ to the fifth power.",
  },
  {
    number: 3,
    tier: "Medium",
    title: "When diagonalization fails",
    setup: "Explain why $A=\\begin{pmatrix}2&1\\\\0&2\\end{pmatrix}$ cannot be diagonalized, and compute $A^{n}$ anyway.",
    steps: [
      { text: "$\\det(A-\\lambda I)=(2-\\lambda)^{2}$, so $\\lambda=2$ twice.", why: "Algebraic multiplicity $2$." },
      { text: "$A-2I=\\begin{pmatrix}0&1\\\\0&0\\end{pmatrix}$ has rank $1$, so its nullspace is $1$-dimensional.", why: "Geometric multiplicity $1$." },
      { text: "Since $1<2$, there is no basis of eigenvectors and no diagonalizing $P$.", why: "Diagonalizability criterion fails." },
      { text: "Write $A=2I+N$ with $N=\\begin{pmatrix}0&1\\\\0&0\\end{pmatrix}$.", why: "Split into scalar plus nilpotent." },
      { text: "$N^{2}=0$, and $2I$ commutes with everything.", why: "Key simplification." },
      { text: "Binomial theorem: $A^{n}=(2I+N)^{n}=2^{n}I+n2^{n-1}N$.", why: "All higher $N$ powers vanish." },
      { text: "$A^{n}=\\begin{pmatrix}2^{n}&n2^{n-1}\\\\0&2^{n}\\end{pmatrix}$.", why: "Assemble." },
      { text: "Test $n=2$: $\\begin{pmatrix}4&4\\\\0&4\\end{pmatrix}$, matching $A^{2}$ computed directly.", why: "Verification." },
    ],
    result: "Not diagonalizable; $A^{n}=\\begin{pmatrix}2^{n}&n2^{n-1}\\\\0&2^{n}\\end{pmatrix}$",
    check: "$n=3$: $\\begin{pmatrix}8&12\\\\0&8\\end{pmatrix}=A\\cdot A^{2}$.",
    mistake: "Applying $A^{n}=PD^{n}P^{-1}$ to a defective matrix.",
  },
  {
    number: 4,
    tier: "Medium",
    title: "Steady state of a Markov chain",
    setup: "Find the long-run distribution for the transition matrix $P=\\begin{pmatrix}0.8&0.3\\\\0.2&0.7\\end{pmatrix}$ (columns sum to $1$).",
    steps: [
      { text: "A steady state satisfies $Px=x$, i.e. $\\lambda=1$ is an eigenvalue.", why: "Every stochastic matrix has eigenvalue $1$." },
      { text: "$P-I=\\begin{pmatrix}-0.2&0.3\\\\0.2&-0.3\\end{pmatrix}$.", why: "Set up the homogeneous system." },
      { text: "Row $1$: $-0.2x_1+0.3x_2=0$, so $x_1=1.5x_2$.", why: "Solve for one variable." },
      { text: "Take $x=(1.5,1)$ before normalizing.", why: "Eigenvectors are only determined up to scale." },
      { text: "Probabilities must sum to $1$: divide by $2.5$.", why: "Normalization for a distribution." },
      { text: "Steady state $x=(0.6,0.4)$.", why: "$1.5/2.5=0.6$." },
      { text: "The other eigenvalue is $\\operatorname{tr}P-1=1.5-1=0.5$.", why: "Trace shortcut." },
      { text: "Since $|0.5|<1$, all initial distributions converge to $(0.6,0.4)$.", why: "The transient mode decays like $0.5^{n}$." },
    ],
    result: "Steady state $(0.6,0.4)$, approached at rate $0.5^{n}$",
    check: "$P(0.6,0.4)^{T}=(0.48+0.12,\\;0.12+0.28)=(0.6,0.4)$.",
    mistake: "Forgetting to normalize, and reporting $(1.5,1)$ as probabilities.",
  },
  {
    number: 5,
    tier: "Medium",
    title: "Orthogonal diagonalization of a symmetric matrix",
    setup: "Orthogonally diagonalize $A=\\begin{pmatrix}5&2\\\\2&2\\end{pmatrix}$.",
    steps: [
      { text: "$p(\\lambda)=\\lambda^{2}-7\\lambda+(10-4)=\\lambda^{2}-7\\lambda+6$.", why: "Trace $7$, determinant $6$." },
      { text: "Factor: $(\\lambda-6)(\\lambda-1)$, so $\\lambda=6,1$.", why: "Real roots, as expected for symmetric $A$." },
      { text: "For $\\lambda=6$: $\\begin{pmatrix}-1&2\\\\2&-4\\end{pmatrix}v=0$ gives $v_1=2v_2$, so $v=(2,1)$.", why: "First eigenvector." },
      { text: "For $\\lambda=1$: $\\begin{pmatrix}4&2\\\\2&1\\end{pmatrix}v=0$ gives $v_2=-2v_1$, so $v=(1,-2)$.", why: "Second eigenvector." },
      { text: "Check orthogonality: $(2,1)\\cdot(1,-2)=2-2=0$.", why: "Spectral theorem guarantee." },
      { text: "Normalize: $q_1=\\tfrac{1}{\\sqrt5}(2,1)$, $q_2=\\tfrac{1}{\\sqrt5}(1,-2)$.", why: "Both have length $\\sqrt5$." },
      { text: "$Q=\\tfrac{1}{\\sqrt5}\\begin{pmatrix}2&1\\\\1&-2\\end{pmatrix}$ satisfies $Q^{T}Q=I$.", why: "Orthogonal matrix." },
      { text: "Then $A=Q\\operatorname{diag}(6,1)Q^{T}$.", why: "Orthogonal diagonalization complete." },
    ],
    result: "$A=Q\\operatorname{diag}(6,1)Q^{T}$ with $Q=\\tfrac{1}{\\sqrt5}\\begin{pmatrix}2&1\\\\1&-2\\end{pmatrix}$",
    check: "$\\det Q=\\tfrac15(-4-1)=-1$, and $\\det A=6=6\\cdot1$.",
    mistake: "Using non-normalized eigenvectors in $Q$ and then claiming $Q^{-1}=Q^{T}$.",
  },
  {
    number: 6,
    tier: "Hard",
    title: "Quadratic form classified by eigenvalues",
    setup: "Classify $q(x,y)=3x^{2}+2xy+3y^{2}$ and find its principal axes.",
    steps: [
      { text: "Matrix form: $q=x^{T}Ax$ with $A=\\begin{pmatrix}3&1\\\\1&3\\end{pmatrix}$.", why: "Off-diagonal entries are half the cross coefficient." },
      { text: "$p(\\lambda)=\\lambda^{2}-6\\lambda+8=(\\lambda-4)(\\lambda-2)$.", why: "Trace $6$, determinant $8$." },
      { text: "Both eigenvalues $4,2$ are positive, so $q$ is positive definite.", why: "Definiteness is read from the spectrum." },
      { text: "Eigenvector for $\\lambda=4$: $(1,1)$; for $\\lambda=2$: $(1,-1)$.", why: "Standard symmetric pattern." },
      { text: "Rotate coordinates by $45^\\circ$: $u=\\tfrac{x+y}{\\sqrt2}$, $v=\\tfrac{x-y}{\\sqrt2}$.", why: "Align axes with eigenvectors." },
      { text: "In new coordinates $q=4u^{2}+2v^{2}$, with no cross term.", why: "Diagonalization removes $xy$." },
      { text: "So the level curve $q=1$ is an ellipse with semi-axes $1/2$ along $u$ and $1/\\sqrt2$ along $v$.", why: "Semi-axis $=1/\\sqrt{\\lambda}$." },
      { text: "Minimum of $q$ on the unit circle is $2$, maximum is $4$.", why: "Rayleigh quotient bounds are the extreme eigenvalues." },
    ],
    result: "Positive definite; $q=4u^{2}+2v^{2}$ with principal axes along $(1,1)$ and $(1,-1)$",
    check: "$q(1,1)=3+2+3=8=4(\\sqrt2)^{2}$, consistent with $u=\\sqrt2,v=0$.",
    mistake: "Putting the full cross coefficient $2$ off the diagonal instead of half of it.",
  },
  {
    number: 7,
    tier: "Hard",
    title: "Decoupling a linear system of ODEs",
    setup: "Solve $x'=\\begin{pmatrix}1&2\\\\0&-1\\end{pmatrix}x$ with $x(0)=(3,1)$.",
    steps: [
      { text: "Eigenvalues from the diagonal: $\\lambda=1,-1$.", why: "Triangular matrix." },
      { text: "For $\\lambda=1$: $(A-I)=\\begin{pmatrix}0&2\\\\0&-2\\end{pmatrix}$ gives $v_2=0$, so $v_1=(1,0)$.", why: "First mode." },
      { text: "For $\\lambda=-1$: $(A+I)=\\begin{pmatrix}2&2\\\\0&0\\end{pmatrix}$ gives $v_2=-v_1$, so $v_2=(1,-1)$.", why: "Second mode." },
      { text: "General solution: $x(t)=c_1e^{t}(1,0)+c_2e^{-t}(1,-1)$.", why: "One exponential per eigenpair." },
      { text: "At $t=0$: $c_1(1,0)+c_2(1,-1)=(3,1)$.", why: "Apply the initial condition." },
      { text: "Second component: $-c_2=1$, so $c_2=-1$.", why: "Solve the easy equation first." },
      { text: "First component: $c_1+c_2=3$, so $c_1=4$.", why: "Back-substitute." },
      { text: "$x(t)=4e^{t}(1,0)-e^{-t}(1,-1)=\\bigl(4e^{t}-e^{-t},\\;e^{-t}\\bigr)$.", why: "Final closed form." },
    ],
    result: "$x(t)=\\bigl(4e^{t}-e^{-t},\\;e^{-t}\\bigr)$ — unstable because one eigenvalue is positive",
    check: "$x(0)=(4-1,1)=(3,1)$, and $x'(0)=(4+1,-1)=(5,-1)=A(3,1)^{T}$.",
    mistake: "Using $e^{\\lambda t}$ with the wrong eigenvector paired to it.",
  },
  {
    number: 8,
    tier: "Hard",
    title: "Population model: dominant eigenvalue and stable stage ratio",
    setup: "A two-stage population evolves by $x_{k+1}=Lx_k$ with the Leslie matrix $L=\\begin{pmatrix}1&2\\\\0.5&0\\end{pmatrix}$. Find the asymptotic growth factor and stable stage ratio.",
    steps: [
      { text: "$\\det(L-\\lambda I)=(1-\\lambda)(-\\lambda)-2(0.5)=\\lambda^2-\\lambda-1$.", why: "Characteristic polynomial of the two-stage Leslie matrix." },
      { text: "Thus $\\lambda_{1,2}=\\dfrac{1\\pm\\sqrt5}{2}$. The dominant eigenvalue is $\\lambda_1=\\varphi=\\dfrac{1+\\sqrt5}{2}\\approx1.618$.", why: "It has strictly larger modulus than the second eigenvalue." },
      { text: "The asymptotic population growth factor is therefore about $1.618$ per step, corresponding to about $61.8\\%$ growth per step once the stable stage structure dominates.", why: "Long-run scaling is set by the dominant eigenvalue." },
      { text: "From the second row of $Lv=\\varphi v$, $0.5v_1=\\varphi v_2$, so $v_1=2\\varphi v_2$.", why: "Solve the eigenvector ratio directly." },
      { text: "Choose $v=(2\\varphi,1)\\approx(3.236,1)$.", why: "Any nonzero scalar multiple represents the same stable stage ratio." },
      { text: "Normalize to proportions: $\\dfrac{2\\varphi}{2\\varphi+1}\\approx0.764$ and $\\dfrac{1}{2\\varphi+1}\\approx0.236$.", why: "Divide each component by the total." },
      { text: "The second eigenvalue is $\\lambda_2=(1-\\sqrt5)/2\\approx-0.618$, so $|\\lambda_2/\\lambda_1|\\approx0.382<1$.", why: "The non-dominant mode decays relative to the dominant mode." },
      { text: "Because $\\lambda_2<0$, deviations from the stable stage ratio can alternate in sign while shrinking, but the normalized state converges to the dominant eigenvector direction.", why: "Negative subdominant mode plus a strict spectral gap." },
    ],
    result: "Dominant growth factor $\\varphi\\approx1.618$ per step with stable stage ratio $2\\varphi:1\\approx3.236:1$",
    check: "$L(2\\varphi,1)^T=(2\\varphi+2,\\varphi)^T=\\varphi(2\\varphi,1)^T$ because $\\varphi^2=\\varphi+1$.",
    mistake: "Calling an eigenvalue dominant without checking its modulus against every other eigenvalue.",
  },
];
